import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Finance from "./pages/Finance";
import Admin from "./pages/Admin";
import AuthCallback from "./pages/AuthCallback";
import SignContract from "./pages/SignContract";
import ReceiptPage from "./pages/ReceiptPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: synchronous check for session_id BEFORE other route logic.
  // Prevents race condition with AuthProvider's /auth/me check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/finanzas" element={<Finance />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/firmar/:token" element={<SignContract />} />
      <Route path="/recibo/:token" element={<ReceiptPage />} />
      <Route path="/mi-cuenta/:token" element={<ClientPortalPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { isDark } = useTheme();
  return (
    <Toaster
      position="bottom-right"
      theme={isDark ? "dark" : "light"}
      toastOptions={{
        style: {
          background: isDark ? "#12131C" : "#FFFFFF",
          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(15,23,42,0.1)",
          color: isDark ? "#fff" : "#0F172A",
          fontFamily: "JetBrains Mono, monospace",
          fontSize: "12px",
        },
      }}
    />
  );
}

function App() {
  // Silence harmless cross-origin "Script error." overlays from CRA dev runtime
  // and unhandled promise rejections from network calls when the backend is sleeping.
  useEffect(() => {
    const onError = (e) => {
      const msg = e?.message || "";
      if (
        msg === "Script error." ||
        msg.includes("ResizeObserver") ||
        msg.includes("Network Error") ||
        msg.includes("Failed to fetch")
      ) {
        e.preventDefault?.();
        e.stopImmediatePropagation?.();
        return true;
      }
      return undefined;
    };
    const onRejection = (e) => {
      const reason = e?.reason || {};
      const msg = reason.message || String(reason);
      if (
        msg.includes("Network Error") ||
        msg.includes("Failed to fetch") ||
        msg.includes("timeout") ||
        msg.includes("AbortError") ||
        msg === "Script error."
      ) {
        e.preventDefault?.();
        return;
      }
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AppRouter />
              <ThemedToaster />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
